// LiveStreamAsideTopPanel.jsx
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import Loading from '../component/Loading.jsx';
import UniversalComponent from '../component/UniversalComponent.jsx';
import ModalTracking from '../component/ModalTracking.jsx';
import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';
import { GiftPreviewProvider } from '../context/GiftPreviewContext.jsx';

import { ModalId } from '../resource/trackingConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import { PRESET_PREVIEW } from '../resource/liveStreamConstants.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { color } from '../style/variables.js';
import CloseIconSource from '../../img/icon-arrows-in-simple.svg';
import OpenIconSource from '../../img/icon-arrows-out-simple.svg';

const SECTIONS = {
  GIFT: 'top-panel-gift-menu',
  KARAOKE: 'top-panel-karaoke-menu',
  PRIVATE: 'top-panel-trigger-private-menu',
};

export const LiveStreamAsideTopPanel = ({
  streamId = '',
  streamStatus = null,
  hasStreamToken = false,
  isAsideTopPanelExpanded = true,
  isOneOnOne = false,
  isPrivateShow = false,
  isExclusivePrivatePerforming = false,
  isMeExclusivePrivateOngoingInitiator = false,
  setLiveStreamPanel = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();

  // Display rules for the one-on-one invite tab:
  // - Streamer is in a timed (one-to-many) performance → hide for everyone.
  // - One-on-one already performing → hide for everyone (including the buyer).
  // - One-on-one preparing → hide when someone else is the buyer; keep it if I'm the buyer.
  const isShouldDisplayPrivateCta =
    (streamStatus === PRESET_PREVIEW || hasStreamToken) &&
    !isPrivateShow &&
    !isExclusivePrivatePerforming &&
    !(isOneOnOne && !isMeExclusivePrivateOngoingInitiator);

  const sections = useMemo(() => {
    const list = [
      {
        id: SECTIONS.GIFT,
        label: t('giftname_default', { ns: TranslationNamespace.GIFT }),
        Component: (
          <GiftPreviewProvider>
            <ModalTracking id="GiftMenu" modalId={ModalId.LiveStream.ModalGift}>
              <UniversalComponent
                filename="Container_GiftMenu"
                streamId={streamId}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/LiveStreamGiftSlider.js')
                }
              />
            </ModalTracking>
          </GiftPreviewProvider>
        ),
      },
      {
        id: SECTIONS.KARAOKE,
        label: t('livestream_command_title_toys_and_tips', {
          ns: TranslationNamespace.LIVESTREAM,
        }),
        Component: (
          <ModalTracking id="KaraokeMenu">
            <UniversalComponent
              filename="Container_KaraokeMenu"
              streamId={streamId}
              loading={<Loading />}
              importer={
                /* #__LOADABLE__ */ () =>
                  import('../container/LivestreamKaraokeMenu.js')
              }
            />
          </ModalTracking>
        ),
      },
    ];
    if (isShouldDisplayPrivateCta) {
      list.push({
        id: SECTIONS.PRIVATE,
        label: t('user_trigger', { ns: TranslationNamespace.LIVESTREAM }),
        Component: (
          <TriggerMenuWrapper>
            <ModalTracking
              id="TriggerPrivateMenu"
              modalId={ModalId.LiveStream.ModalUserTrigger}
            >
              <UniversalComponent
                filename="Container_TriggerPrivateMenu"
                streamId={streamId}
                loading={<Loading />}
                importer={
                  /* #__LOADABLE__ */ () =>
                    import('../container/TriggerPrivateMenu.js')
                }
              />
            </ModalTracking>
          </TriggerMenuWrapper>
        ),
      });
    }
    return list;
  }, [isShouldDisplayPrivateCta, streamId, t]);

  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id);

  const sectionsById = useMemo(() => {
    return objectifyArrayById({ array: sections });
  }, [sections]);

  const handleTabChange = useCallback(
    (_, nextValue) => {
      if (!nextValue) return;
      const nextSection = sectionsById[nextValue];
      if (!nextSection || !nextSection?.id) return;
      setActiveSectionId(nextValue);
      setLiveStreamPanel({
        isAsideTopPanelExpanded: true,
      }); // because the top panel might closed, open aside top panel when active tab changed
    },
    [sectionsById, setLiveStreamPanel]
  );

  const activeSection = sectionsById[activeSectionId] || null;

  const toggleAsideTopPanel = useCallback(() => {
    setLiveStreamPanel({
      isAsideTopPanelExpanded: !isAsideTopPanelExpanded,
    });
  }, [isAsideTopPanelExpanded, setLiveStreamPanel]);

  return (
    <StyledLiveStreamAsideTopPanel
      isAsideTopPanelExpanded={isAsideTopPanelExpanded}
    >
      <LiveStreamAsideTopPanelHeader>
        <TabsWrapper>
          <Tabs isAnchorTab value={activeSectionId} onChange={handleTabChange}>
            {sections.map(section => (
              <Tab
                key={section.id}
                isAnchorTab
                value={section.id}
                label={section.label}
                isActive={activeSectionId === section.id}
              />
            ))}
          </Tabs>
        </TabsWrapper>
        <CloseButton
          key={isAsideTopPanelExpanded ? 'close' : 'open'}
          onClick={toggleAsideTopPanel}
        >
          <img
            src={isAsideTopPanelExpanded ? CloseIconSource : OpenIconSource}
            alt="close"
          />
        </CloseButton>
      </LiveStreamAsideTopPanelHeader>
      {isAsideTopPanelExpanded && !!activeSectionId && activeSection?.Component}
    </StyledLiveStreamAsideTopPanel>
  );
};

LiveStreamAsideTopPanel.propTypes = {
  streamId: PropTypes.string,
  streamStatus: PropTypes.string,
  hasStreamToken: PropTypes.bool,
  isOneOnOne: PropTypes.bool,
  isPrivateShow: PropTypes.bool,
  isExclusivePrivatePerforming: PropTypes.bool,
  isMeExclusivePrivateOngoingInitiator: PropTypes.bool,
  isAsideTopPanelExpanded: PropTypes.bool,
  setLiveStreamPanel: PropTypes.func,
};

const StyledLiveStreamAsideTopPanel = styled.div`
  ${({ isAsideTopPanelExpanded }) =>
    isAsideTopPanelExpanded
      ? css`
          border-bottom: 1px solid ${color.neutral['20_alpha_40']};
        `
      : css``}
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const LiveStreamAsideTopPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
`;

const CloseButton = styled.button`
  ${ResetButtonStyle};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  > img {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }
`;

const TabsWrapper = styled.div`
  flex: 1;
`;

const TriggerMenuWrapper = styled.div`
  padding: 0px 16px 16px 16px;
`;

export default LiveStreamAsideTopPanel;
